import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { decode } from 'js-base64';
import { set, setDefaultsLoaded } from "../store/configSlice";
import { showPreloader, hidePreloader, showPopover, showModal, showTooltip, hideTooltip } from "../store/navSlice";

import * as THREE from "three";
import Stats from "stats.js";
import FpsMonitor from "./viewer-components/fps-monitor";
import Camera from "./viewer-components/camera";
import Environment from "./viewer-components/environment";
import Wheelchair from "./viewer-components/wheelchair";
import HotspotsManager from "./viewer-components/hotspots-manager";
import configMap from "../data/config";
import materialsMap from "../data/materials";
import pkg from "../../package.json";
import MaterialGroups from './viewer-components/materialGroups'

import "./Viewer.scss";
import { ReactComponent as Preloader } from "../assets/images/preloader.svg";
import { ReactComponent as IconPlus } from "../assets/images/plus.svg";
import { ReactComponent as IconMinus } from "../assets/images/minus.svg";
import { ReactComponent as IconRefresh } from "../assets/images/refresh.svg";

const mapState = (state) => {
  const config = state.config;
  const {
    activePart,
    drawerOpen,
    nitrumOpen,
    preloaderVisible,
    backgroundVisible,
  } = state.nav;

  return {
    activePart,
    config,
    drawerOpen,
    nitrumOpen,
    preloaderVisible,
    backgroundVisible: backgroundVisible && !drawerOpen && !nitrumOpen,
    viewerShrinked: drawerOpen || nitrumOpen,
  };
};

const mapDispatch = {
  set,
  setDefaultsLoaded,
  showPreloader,
  hidePreloader,
  showPopover,
  showModal,
  showTooltip,
  hideTooltip,
};

class Viewer extends Component {

  /**
   * Enable/disable shadows on the environment
   */
  get UseShadows() {
    return this._useShadows;
  }

  set UseShadows(useShadows) {
    this.renderer.shadowMap.enabled = useShadows;
    this.wheelchair.UseShadows = useShadows;
    this.environment.UseShadows = useShadows;

    this._useShadows = useShadows;
  }

  constructor(props) {
    super();
    this.shareUrlUsesHash = true;
    this.shareUrlVersion = 1;
    this.debug = false;
    this.basepath = (pkg.homepage || "");
  }

  /**
   * Initialize Viewer
   * - Setup THREE components, Environment, Wheelchair, Hotspots
   * - Sets global event listerners (mouse, resize)
   * - Initializes the animation loop
   */
  componentDidMount() {
    this.isMobile = window.innerWidth < 992;
    this.lastWidth = this.mount.offsetWidth; // used to autoadapt webgl context

    // SETUP THREE SCENE
    this.stats = this.debug && this.createStats();
    this.renderer = this.createRenderer();
    this.scene = this.createScene();
    this.camera = new Camera(this.renderer, this.isMobile);
    this.fpsMonitor = new FpsMonitor();

    // SETUP ENVIRONMENT
    // @todo check if HDR setup on materials triggering before materials
    // are loaded/set is OK
    this.environment = new Environment(this.scene, this.renderer);
    this.environment.on("onHDRLoaded", this.onHDRLoaded.bind(this));

    // SETUP WHEELCHAIR (+ load defaults)
    this.wheelchair = new Wheelchair(this.scene, this.renderer, this.props.config._country);
    this.wheelchair.on("partsloaded", this.onWheelchairLoaded.bind(this));

    // Enable Shadows
    this.UseShadows = true;

    // Drop frames and we will kill shadows
    this.dropFramesThreshold = 45; // below X fps
    this.dropFramesThresholdCounter = 5; // for X seconds

    // Load default configuration
    this.loadDefaultConfig();
    this.loadSavedConfig();

    // SETUP HOSTPOTS
    this.hotspotsManager = new HotspotsManager(
      this.scene,
      this.renderer,
      this.camera.camera,
      this.mount
    );
    this.hotspotsManager.on("hotspotOver", this.onHotspotOver.bind(this));
    this.hotspotsManager.on("hotspotOut", this.onHotspotOut.bind(this));
    this.hotspotsManager.on(
      "hotspotSelected",
      this.onHotspotSelected.bind(this)
    );
    this.hotspotsManager.on(
      "hotspotSelectedDelayed",
      this.onHotspotSelectedDelayed.bind(this)
    );

    // LISTENERS

    // webgl context
    this.mount.addEventListener(
      "resize",
      this.onWindowResize.bind(this),
      false
    );

    // display webgl
    this.mount.appendChild(this.renderer.domElement);
    this.onWindowResize(); // trigger initial resize
    this.animate(); // start animation loop   
  }

  /**
   * Handles initial parts loading and configurator changes
   * Handles background toggling
   * @param {*} prevProps
   */
  componentDidUpdate(prevProps) {
    if (this.props.backgroundVisible !== prevProps.backgroundVisible) {
      this.environment.toggleBackground(this.props.backgroundVisible);
      return;
    }

    if (this.props.activePart !== prevProps.activePart) {
      this.onCategoryChange(this.props.activePart, prevProps.activePart);
      return;
    }

    const config = this.props.config;
    const prevConfig = prevProps.config;
    const { _updatedPart, _updatedAttr } = config;

    // IGNORE CHANGES UNTIL DEFAULTS ARE LOADED
    if (!config._defaultsLoaded) {
      this.debug &&
        console.log("[Viewer] Got changes but defaults aren't set yet");
      return;
    }

    // LOAD DEFAULT PARTS ON _defaultsLoaded change (one time only)
    if (config._defaultsLoaded !== prevConfig._defaultsLoaded) {
      this.debug && console.log("[Viewer] Defaults set, loading default parts");
      this.loadWheelchair();
      return;
    }

    // SWAP ASSETS REPOSITIONED BY FRAME ANGE/INSET
    if (config._framePath !== prevConfig._framePath) {
      this.debug &&
        console.log("[Viewer] Frame changed, updating repositionable parts");
      this.updateFrameRepositionableParts();
      return;
    }

    // SWAP PUSHBAR REPOSITIONED BY BACKREST (push_handle_and_back)
    if (
      config._pushbarPath !== prevConfig._pushbarPath ||
      config.BACKREST.backrest !== prevConfig.BACKREST.backrest || 
      config.BACKREST.push_handle !== prevConfig.BACKREST.push_handle
    ) {
      this.debug &&
        console.log("[Viewer] Push Handles changed, updating repositionable parts");
      this.updateBackrestRepositionableParts();
      return;
    }

    // Handle constraints for armrest
    if (config.SIDEGUARDS.armrests !== prevConfig.SIDEGUARDS.armrests) {
      this.debug &&
        console.log("[Viewer] Armrest changed, updating sideguards");
      this.updateArmrestSideguards();
      // no return - let pass through to handle actual armrest change
    }

    // SWAP 'LOOSE' PARTS
    if (_updatedPart) {
      const newVal = config[_updatedPart][_updatedAttr];
      const oldVal =
        prevConfig[_updatedPart].hasOwnProperty(_updatedAttr) &&
        prevConfig[_updatedPart][_updatedAttr];

      if (newVal !== oldVal) {
        this.debug && console.log("UPDATE PART ATTR", _updatedPart, _updatedAttr);
        this.updatePartAttribute(_updatedPart, _updatedAttr, newVal, oldVal);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // WHEELCHAIR UPDATING
// ---------------------------------------------------------------------------

  /**
   * Sets default parts in Redux
   *
   * This initial/default changes are ignored by componentDidUpdate
   * via the state flag config._defaultsLoaded
   */
  loadDefaultConfig() {
    const { _region } = this.props.config;

    this.debug && console.groupCollapsed("SET DEFAULTS");

    for (let partKey in configMap) {
      this.debug && console.groupCollapsed(partKey);

      let attributes = configMap[partKey];
      for (let attrKey in attributes) {
        this.debug && console.groupCollapsed(attrKey);

        // this.debug && console.log("ALL", attributes[attrKey]);
        this.debug && console.log("Type", attributes[attrKey].optionsType);

        // MATERIAL/COLOR
        if (attributes[attrKey].optionsType === "color") {
          let materialSet = attributes[attrKey].materialSet;
          if (!materialSet) {
            this.debug && console.log("No Material Set");
            this.debug && console.groupEnd();
            continue;
          }

          let defaultMaterial = attributes[attrKey].defaultMaterial;

          if (typeof defaultMaterial === "object") {
            defaultMaterial = defaultMaterial[_region];
          }

          this.debug &&
            console.log("Default", defaultMaterial ? defaultMaterial : "-");

          this.props.set({
            part: partKey,
            attr: attrKey,
            option: {
              value: defaultMaterial,
            },
          });

          // OTHER: MODEL, CONTEXTUAL, DECLARATIVE
        } else {
          let options = attributes[attrKey].options;

          if (attributes[attrKey].regional) {
            options = options[_region];
          }

          if (!options) {
            this.debug && console.log("No options");
            this.debug && console.groupEnd();
            continue;
          }

          this.debug && console.log("Options", options);

          let defaults = options.filter((option) => {
            return option.hasOwnProperty("default") && option.default;
          });

          this.debug &&
            console.log("Default", defaults.length ? defaults[0] : "-");

          if (defaults.length) {
            this.props.set({
              part: partKey,
              attr: attrKey,
              option: defaults[0],
            });
          }
        }

        this.debug && console.groupEnd();
      }

      this.debug && console.groupEnd();
    }

    this.debug && console.groupEnd();
  }

  reloadDefaultConfig() {
    this.wheelchair.off("partsloaded");
    this.props.setDefaultsLoaded(false);
    this.loadDefaultConfig();
    setTimeout(() => {
      this.props.setDefaultsLoaded(true);      
    }, 100);
  }

  loadSavedConfig() {
    const { _region } = this.props.config;
    const url = new URL(window.location.toString());
    const hasSavedConfig = this.shareUrlUsesHash
      ? url.hash.indexOf("/share/") !== -1
      : url.pathname.indexOf("/share/") !== -1;

    // Parse hybrid url param
    if (_region === "EU" && url.searchParams.has("frame")) {
      const frameParam = url.searchParams.get("frame");
      if (frameParam === "hybrid") {
        this.props.set({
          part: "FRAME",
          attr: "type",
          option: {
            value: "HYBRID",
          },
        });
      }
    }

    if (!hasSavedConfig) {
      // Set state flag config._defaultsLoaded to true to start responding
      // to config changes in componentDidUpdate
      this.props.setDefaultsLoaded(true);
      return;
    }

    let savedConfig = this.shareUrlUsesHash
      ? url.hash.replace("#/share/", "")
      : url.pathname.replace(this.basepath + "/share/", "");

    savedConfig = decode(savedConfig);
    savedConfig = savedConfig.split("-");

    // Check share link version
    if (/v[1-9]/.test(savedConfig[0])) {
      const version = parseInt(savedConfig[0].replace("v", ""), 10);
      if (version < this.shareUrlVersion) {
        this.debug && console.log(`Share link version ${version} is no longer supported`);
        this.props.setDefaultsLoaded(true);
        return;  
      }

      // remove version element from array and continue
      savedConfig.shift();
    } else {
      this.debug && console.log("Share link has no version");
      this.props.setDefaultsLoaded(true);
      return;
    }

    // Validate we have the same amount of parts
    if (savedConfig.length !== Object.keys(configMap).length) {
      this.debug && console.log("Invalid config link");
      this.props.setDefaultsLoaded(true);
      return;
    }

    this.debug && console.groupCollapsed("SET SAVED");

    let partI = -1;
    for (let partKey in configMap) {
      partI++;
      const savedPartConfig = savedConfig[partI].split("");
      this.debug && console.groupCollapsed(partKey);

      let attributes = configMap[partKey];
      let attrI = -1;

      for (let attrKey in attributes) {
        if (attributes[attrKey].hidden) {
          continue;
        }

        attrI += 2;

        const savedAttrKey = savedPartConfig[attrI - 1] + savedPartConfig[attrI];
        console.log(savedAttrKey);
        this.debug && console.groupCollapsed(attrKey);
        this.debug && console.log("linkKey", savedAttrKey);

        // this.debug && console.log("ALL", attributes[attrKey]);
        this.debug && console.log("Type", attributes[attrKey].optionsType);

        // MATERIAL/COLOR
        if (attributes[attrKey].optionsType === "color") {
          let materialSet = attributes[attrKey].materialSet;
          if (!materialSet) {
            this.debug && console.log("No Material Set");
            this.debug && console.groupEnd();
            continue;
          }

          let savedMaterial = this._findMaterialByLinkKey(savedAttrKey);

          if (!savedMaterial) {
            continue;
          }

          this.debug &&
            console.log("Saved", savedMaterial ? savedMaterial : "-");

          this.props.set({
            part: partKey,
            attr: attrKey,
            option: {
              value: savedMaterial,
            },
          });

          // OTHER: MODEL, CONTEXTUAL, DECLARATIVE
        } else {
          const isRegionalOption = attributes[attrKey].regional || false;
          const options = isRegionalOption
            ? attributes[attrKey].options[_region]
            : attributes[attrKey].options;

          if (!options) {
            this.debug && console.log("No options");
            this.debug && console.groupEnd();
            continue;
          }

          this.debug && console.log("Options", options);

          let saved = options.filter((option) => {
            return (
              option.hasOwnProperty("linkKey") &&
              option.linkKey === savedAttrKey
            );
          });

          this.debug && console.log("Saved", saved.length ? saved[0] : "-");

          if (saved.length) {
            this.props.set({
              part: partKey,
              attr: attrKey,
              option: saved[0],
            });
          }
        }

        this.debug && console.groupEnd();
      }

      this.debug && console.groupEnd();
    }

    this.props.setDefaultsLoaded(true);
  }

  loadWheelchair() {
    const { _framePath, _pushbarPath, _region } = this.props.config;

    const forks        = this.getSrcByOptionValue("CASTERS", "forks");
    const casters      = this.getSrcByOptionValue("CASTERS", "casters");
    const footrest     = this.getSrcByOptionValue("FOOTREST", "footrest");
    const cushion      = this.getSrcByOptionValue("SEAT_SLING", "cushion");
    const backrest     = this.getSrcByOptionValue("BACKREST", "backrest");
    const push_handles = this.getSrcByOptionValue("BACKREST", "push_handles");
    const push_bar     = this.getSrcByOptionValue("BACKREST", "push_bar");
    const armrests     = this.getSrcByOptionValue("SIDEGUARDS", "armrests");
    const sideguards   = this.getSrcByOptionValue("SIDEGUARDS", "sideguards");
    const rear_wheels  = this.getSrcByOptionValue("REAR_WHEELS", "rear_wheels");
    const breaks       = this.getSrcByOptionValue("WHEEL_LOCKS", "brakes");
    const safety       = this.getSrcByOptionValue("ADDONS", "safety");
    const led_lights   = this.getSrcByOptionValue("FRAME", "led_lights");
    const type         = this.getSrcByOptionValue("FRAME", "type");
    const f55          = this.getSrcByOptionValue("ADDONS", "f55");

    const parts = {
      frame_common : `Common_Parts/Frame_Common.glb`,
      f55          : f55 ? `${_framePath}/${f55}` : "false",
      lower_frame  : `${_framePath}/Lower_Frame/${_region}_Lower_Frame.glb`,
      type         : type ? `${_framePath}/${type}` : "false",
      forks        : `${_framePath}/${forks}`,
      casters      : `${_framePath}/${casters}`,
      footrest     : `${_framePath}/${footrest}`,
      sling        : "Slings/Sling_1.glb",
      bag_common   : "Slings/Utility_Bag_LH.glb",
      cushion      : `${cushion}`,
      backrest     : `${backrest}`,
      push_handles : `${push_handles}`,
      push_bar     : `${_pushbarPath}/${push_bar}`,
      armrests     : `${armrests}`,
      sideguards   : `${sideguards}`,
      rear_wheels  : `${rear_wheels}`,
      brakes       : `${breaks}`,
      safety       : `${safety}`,
      led_lights   : `${_framePath}/${led_lights}`,
    };

    const materialGroups = this.getBaseMaterialGroup();

    this.props.showPreloader();
    this.wheelchair
      .loadParts(parts, materialGroups, true)
      .finally(() => this.props.hidePreloader());

    this._updateHotspots(this.currentCategory);
  }

  // ---------------------------------------------------------------------------

  updateFrameRepositionableParts() {
    const { _framePath, _region } = this.props.config;
    const forks = this.getSrcByOptionValue("CASTERS", "forks");
    const casters = this.getSrcByOptionValue("CASTERS", "casters");
    const footrest = this.getSrcByOptionValue("FOOTREST", "footrest");
    const led_lights = this.getSrcByOptionValue("FRAME", "led_lights");
    const type = this.getSrcByOptionValue("FRAME", "type");
    const f55 = this.getSrcByOptionValue("ADDONS", "f55");

    // we compute this way because removable parts should pass a false value
    // casted as string, but in this case we have the frame_path prepended
    const led_lights_path = !led_lights ? false : `${_framePath}/${led_lights}`;
    const type_path = !type ? false : `${_framePath}/${type}`;
    const f55_path = !f55 ? false : `${_framePath}/${f55}`;

    const parts = {
      lower_frame: `${_framePath}/Lower_Frame/${_region}_Lower_Frame.glb`,
      forks: `${_framePath}/${forks}`,
      casters: `${_framePath}/${casters}`,
      footrest: `${_framePath}/${footrest}`,
      led_lights: `${led_lights_path}`,
      type: `${type_path}`,
      f55: `${f55_path}`
    };

    if (!f55_path) {
      this.wheelchair.unloadPart("f55");
    }

    const materialGroups = [
      ...MaterialGroups.frameMaterials(configMap , this.props),
      ...MaterialGroups.footRestMaterials(configMap , this.props),
      ...MaterialGroups.casterMaterials(configMap , this.props),
      ...MaterialGroups.addOnMaterials(configMap , this.props),
    ];

    this.props.showPreloader();
    this.wheelchair
      .loadParts(parts, materialGroups)
      .finally(() => this.props.hidePreloader());

    this._updateHotspots(this.currentCategory);
  }

  updateBackrestRepositionableParts() {
    const parts = {};

    // BACKREST
    const backrest = this.getSrcByOptionValue("BACKREST", "backrest");
    parts.backrest = `${backrest}`;

    // PUSH HANDLES
    const push_handles = this.getSrcByOptionValue("BACKREST", "push_handles");
    
    if (!push_handles) {
      this.wheelchair.unloadPart("push_handles");
    } else {
      parts.push_handles = `${push_handles}`;
    }

    // PUSH BAR

    const push_bar = this.getSrcByOptionValue("BACKREST", "push_bar");
    
    if (!push_bar) {
      this.wheelchair.unloadPart("push_bar");
    } else {
      const { _pushbarPath } = this.props.config;
      parts.push_bar = `${_pushbarPath}/${push_bar}`;
    }

    const materialGroups = [
      ...MaterialGroups.backrestMaterials(configMap , this.props),
      ...MaterialGroups.frameMaterials(configMap , this.props),
    ];

    this.props.showPreloader();
    this.wheelchair
      .loadParts(parts, materialGroups)
      .finally(() => this.props.hidePreloader());

    this._updateHotspots(this.currentCategory);
  }

  updateArmrestSideguards() {
    const sideguards = this.getSrcByOptionValue("SIDEGUARDS", "sideguards");
    
    if (!sideguards) {
      this.wheelchair.unloadPart("sideguards");
      return;
    }

    const parts = {
      sideguards: `${sideguards}`
    };

    const materialGroups = [
      ...MaterialGroups.sideGuardsMaterials(configMap, this.props),
      ...MaterialGroups.frameMaterials(configMap, this.props)
    ];

    this.props.showPreloader();
    this.wheelchair
      .loadParts(parts, materialGroups)
      .finally(() => this.props.hidePreloader());
  }

  updatePartAttribute(part, attr, newVal, oldVal) {
    const { _region } = this.props.config;

    if (!this.validatePartAttributeValue(part, attr, newVal)) {
      return;
    }

    this.debug &&
      console.log("updatePartAttribute():", part, attr, newVal, oldVal);

    const assetType = configMap[part][attr].assetType;

    if (assetType === "material") {
      const targets = configMap[part][attr].targets;

      this.props.showPreloader();
      this.wheelchair
        .updatePartsMaterials(targets, newVal)
        .finally(() => this.props.hidePreloader());
    } else if (assetType === "model") {
      const isRegionalOption = configMap[part][attr].regional || false;
      const options = isRegionalOption
        ? configMap[part][attr].options[_region]
        : configMap[part][attr].options;
      const newOption = options.find((el) => el.value === newVal);

      this.debug && console.log(`SET ${part} ${attr}:`, newVal, newOption);

      let src = newOption.src;

      this._updateHotspots(this.currentCategory);

      // "NONE" case
      if (src === false) {
        this.wheelchair.unloadPart(attr);
        return;
      }

      // Handle zoom out on F55 load
      // F55 unload is stopped at the previous "NONE" case
      if  (part === "ADDONS" && attr === 'f55') {
        this.camera.zoomCameraTo(1.04, 0.75);
        setTimeout(() => {
          this.updatePartAttribute_LoadPart(part, attr, newOption, src);
        }, 375); // less time than animation because of easing curve
        return;
      }

      this.updatePartAttribute_LoadPart(part, attr, newOption, src);
    }    
  }

  updatePartAttribute_LoadPart(part, attr, newOption, src) {
    const { _region } = this.props.config;

    if (newOption.hasOwnProperty("regional") && newOption.regional) {
      src = newOption.src[_region];
    } else if (/(footrest|casters|forks|led_lights|type|f55)/.test(attr)) {
      const { _framePath } = this.props.config;
      src = `${_framePath}/${src}`;
    } else if (/(push_bar)/.test(attr)) {
      const { _pushbarPath } = this.props.config;
      src = `${_pushbarPath}/${src}`;
    }

    const parts = {
      [attr]: src,
    };

    const materialGroups = this.getBaseMaterialGroup();

    this.props.showPreloader();
    this.wheelchair
      .loadParts(parts, materialGroups)
      .finally(() => this.props.hidePreloader());
  }

  validatePartAttributeValue(part, attr, value) {
    const { _region } = this.props.config;

    if (!configMap.hasOwnProperty(part)) {
      console.error(`Missing definitions for ${part} part`);
      return false;
    }

    if (!configMap[part].hasOwnProperty(attr)) {
      console.error(`Missing definitions for ${part} ${attr} attribute`);
      return false;
    }

    if (!configMap[part][attr].hasOwnProperty("assetType")) {
      console.error(`Missing assetType definition for ${part} ${attr}`);
      return false;
    }

    const assetType = configMap[part][attr].assetType;

    if (assetType === "declarative") {
      return true;
    } else if (assetType === "contextual") {
      return true;
    } else if (assetType === "material") {
      // @todo validate
      // should validate material.id - if we have multiple sets, we need
      // to validate via unique id to avoid false positive
      return true;
    } else if (assetType === "model") {
      const isRegionalOption = configMap[part][attr].regional || false;
      const options = isRegionalOption
        ? configMap[part][attr].options[_region]
        : configMap[part][attr].options;
      const option = options.find((el) => el.value === value);

      if (!option) {
        console.error(
          `Missing definitions for ${part} ${attr} ${value} option`
        );
        return false;
      }

      return true;
    } else {
      console.error(
        `Unknown assetType definition for ${part} ${attr}:`,
        assetType
      );

      return false;
    }
  }

  getSrcByOptionValue(part, attr) {
    const config = this.props.config;
    const {_region} = config;

    const isRegionalOption = configMap[part][attr].regional || false;
    const options = isRegionalOption
      ? configMap[part][attr].options[_region]
      : configMap[part][attr].options;

    const option = options.find((el) => el.value === config[part][attr]);

    if (!option) {
      return false;
    }

    if (option.hasOwnProperty("regional") && option.regional) {
      return option.src[_region];
    };

    return option.src;
  }

  getBaseMaterialGroup() {
    return [
      ...MaterialGroups.rearWheelsMaterials(configMap, this.props),
      // Important to process sideguard materials first as there is a conflict
      // with frame materials on the aluminium sideguards
      ...MaterialGroups.sideGuardsMaterials(configMap, this.props),
      ...MaterialGroups.frameMaterials(configMap, this.props),
      ...MaterialGroups.backrestMaterials(configMap, this.props),
      ...MaterialGroups.casterMaterials(configMap, this.props),
      ...MaterialGroups.seatSlingMaterials(configMap, this.props),
      ...MaterialGroups.footRestMaterials(configMap, this.props),
      ...MaterialGroups.wheelLocksMaterials(configMap, this.props),
      ...MaterialGroups.addOnMaterials(configMap, this.props),
    ];
  }

  _findMaterialByLinkKey(linkKey) {
    const materials = materialsMap.MATERIALS;

    for (let name in materials) {
      const matLinkKey = materials[name].linkKey;
      if (matLinkKey === linkKey) {
        return name;
      }
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  // INITIALIZE
  // ---------------------------------------------------------------------------

  createRenderer() {
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);

    renderer.outputEncoding = true;
    renderer.gammaFactor = 1.0;
    renderer.autoClear = true;

    renderer.setClearColor(0x000000, 0);
    renderer.physicallyCorrectLights = true;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMappingExposure = 1;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    return renderer;
  }

  createScene() {
    const scene = new THREE.Scene();
    // scene.background = new THREE.Color( 0x000000 );

    return scene;
  }

  createStats() {
    const stats = new Stats();
    stats.showPanel(2); // 0: fps, 1: ms, 2: mb, 3+: custom
    stats.dom.style.right = "0px";
    stats.dom.style.left = "auto";
    document.body.appendChild(stats.dom);

    return stats;
  }

  // ---------------------------------------------------------------------------
  // GLOBAL HANDLERS
  // ---------------------------------------------------------------------------

  onHDRLoaded(hdr) {
    this.wheelchair.setHdr(hdr);
  }

  onWheelchairLoaded() {
    this.camera.moveCamera(90, 220, 1, 1, 1, () => {
      this.hotspotsManager.createHotSpots();
      this.fpsMonitor.enable();
    });
  }

  onWindowResize() {
    this.lastWidth = this.mount.offsetWidth;
    this.camera.camera.aspect = this.mount.offsetWidth / window.innerHeight;
    this.camera.camera.updateProjectionMatrix();
    this.renderer.setSize(this.mount.offsetWidth, window.innerHeight);
  }

  // ---------------------------------------------------------------------------
  // HOTSPOTS
  // ---------------------------------------------------------------------------

  onCategoryChange(currentCategory, prevCategory) {
    this.currentCategory = currentCategory;

    this._updateHotspots(currentCategory);
    if (!currentCategory) {
      // Drawer was closed
    }
  }

  _updateHotspots(category) {
    const { angle, inset, type, axle_tube, led_lights } = this.props.config.FRAME;
    const { utility_bag_right, cushion } = this.props.config.SEAT_SLING;
    const { push_bar, push_handle_and_back } = this.props.config.BACKREST;
    const { sideguards } = this.props.config.SIDEGUARDS;
    const { footrest } = this.props.config.FOOTREST;
    const { casters, forks } = this.props.config.CASTERS;
    const { rear_wheels } = this.props.config.REAR_WHEELS;
    const { brakes } = this.props.config.WHEEL_LOCKS;
    const { safety } = this.props.config.ADDONS;

    const parts = [
      angle, inset, type, axle_tube, led_lights,
      utility_bag_right, cushion,
      push_bar, push_handle_and_back, 
      sideguards,
      footrest,
      casters, forks,
      rear_wheels,
      brakes,
      safety
    ]

    this.hotspotsManager.updateHotSpots(category, parts);
  }

  onHotspotOver(hotspot) {
    const { transKey } = hotspot;
    const { x, y } = hotspot.screenPosition;
    this.props.showTooltip({ transKey, x, y });
  }

  onHotspotOut() {
    this.props.hideTooltip();
  }

  onHotspotSelected(hotspot) {
    const { cameraX, cameraY, zoom } = hotspot; // transKey
    this.camera.moveCamera(cameraX, cameraY, zoom, 1);
    this.props.hideTooltip();
    // window.ga('send', 'event', "visualiser", "hotspot", transKey);
  }

  onHotspotSelectedDelayed(hotspot) {
    const { type, transKey, src } = hotspot;
    const { x, y } = hotspot.screenPosition;

    if (type === "video") {
      this.props.showModal({ transKey, src });
    } else if (type === "image" || type === "text") {
      this.props.showPopover({ transKey, src, x, y });
    }
  }

  // ---------------------------------------------------------------------------
  // ZOOM
  // ---------------------------------------------------------------------------

  onZoomIn() {
    this.camera.zoomCamera(1);
  }

  onZoomOut() {
    this.camera.zoomCamera(-1);
  }

  // ---------------------------------------------------------------------------
  // RENDER / ANIMATE
  // ---------------------------------------------------------------------------

  animate = () => {
    if (!this.isMobile && this.lastWidth !== this.mount.offsetWidth) {
      this.onWindowResize();
    }

    this.fpsMonitor.begin();
    
    this.debug && this.stats.begin();
    
    this.camera.update();
    this.wheelchair.update();
    this.hotspotsManager.update();
    this.renderer.render(this.scene, this.camera.camera);
    
    this.debug && this.stats.end();
    
    const fps = this.fpsMonitor.end();

    if (fps && fps < this.dropFramesThreshold) {
      this.dropFramesThresholdCounter--;
      if (this.dropFramesThresholdCounter === 0) {
        console.log("Disabling shadows");
        this.UseShadows = false;
        this.fpsMonitor.disable();
      }
    }    

    requestAnimationFrame(this.animate);
  };

  render() {
    const { t,preloaderVisible, viewerShrinked } = this.props;

    return (
      <div className="app-viewer">
        <div
          ref={(ref) => (this.mount = ref)}
          className={`content ${viewerShrinked ? "shrinked" : ""}`}
        />
        <div className={`controls ${viewerShrinked ? "" : "show"}`}>
          <div className="control">
            <button
              className="btn btn-circle btn-link"
              onClick={this.onZoomIn.bind(this)}
            >
              <IconPlus className="icon" />
            </button>
            <div className="label">{t("zoom_in")}</div>
          </div>
          <div className="control">
            <button
              className="btn btn-circle btn-link my-15"
              onClick={this.onZoomOut.bind(this)}
            >
              <IconMinus className="icon" />
            </button>
            <div className="label">{t("zoom_out")}</div>
          </div>
          <div className="control">
            <button
              className="btn btn-circle btn-link"
              onClick={this.reloadDefaultConfig.bind(this)}
            >
              <IconRefresh className="icon" />
            </button>
            <div className="label">{t("refresh")}</div>
          </div>
        </div>
        <div
          className={`preloader ${preloaderVisible ? "show" : ""} ${
            viewerShrinked ? "shrinked" : ""
          }`}
        >
          <div className="icon-wrapper">
            <Preloader />
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation()(connect(mapState, mapDispatch)(Viewer));
